import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MobilePageHeaderContext = createContext(null);

export function MobilePageHeaderProvider({ children }) {
  const [overrideTitle, setOverrideTitle] = useState(null);
  const [trailingAction, setTrailingAction] = useState(null);
  const [backAction, setBackAction] = useState(null);
  const { pathname } = useLocation();
  const prevPathnameRef = useRef(null);

  // Clear overrides only on real navigations — not on the initial mount, so
  // child hooks (title/trailing/back) that register in the same tick are not wiped.
  useEffect(() => {
    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = pathname;
      return;
    }
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    setOverrideTitle(null);
    setTrailingAction(null);
    setBackAction(null);
  }, [pathname]);

  const value = useMemo(
    () => ({
      overrideTitle,
      setOverrideTitle,
      trailingAction,
      setTrailingAction,
      backAction,
      setBackAction,
    }),
    [overrideTitle, trailingAction, backAction],
  );

  return (
    <MobilePageHeaderContext.Provider value={value}>
      {children}
    </MobilePageHeaderContext.Provider>
  );
}

/** Permite às páginas de detalhe substituir o título do header mobile. */
export function useMobilePageHeaderTitle(title) {
  const ctx = useContext(MobilePageHeaderContext);

  useEffect(() => {
    if (!ctx || !title) return undefined;
    ctx.setOverrideTitle(title);
    return () => ctx.setOverrideTitle(null);
  }, [ctx, title]);
}

/**
 * Acção opcional à direita do header mobile (ex.: badge do método no Orçamento).
 * @param {{ label?: string, onClick?: () => void, ariaLabel?: string } | null} action
 */
export function useMobilePageHeaderTrailing(action) {
  const ctx = useContext(MobilePageHeaderContext);
  const setTrailingAction = ctx?.setTrailingAction;
  const onClickRef = useRef(action?.onClick);

  useEffect(() => {
    onClickRef.current = action?.onClick;
  }, [action?.onClick]);

  useEffect(() => {
    if (!setTrailingAction) return undefined;
    if (!action?.label) {
      setTrailingAction(null);
      return () => setTrailingAction(null);
    }
    setTrailingAction({
      label: action.label,
      ariaLabel: action.ariaLabel,
      onClick: () => onClickRef.current?.(),
    });
    return () => setTrailingAction(null);
  }, [action?.label, action?.ariaLabel, setTrailingAction]);
}

/**
 * Substitui o Link «voltar» do MobileRouteHeader por um handler (ex.: passo anterior do wizard).
 * @param {(() => void) | null | undefined} onBack
 */
export function useMobilePageHeaderBack(onBack) {
  const ctx = useContext(MobilePageHeaderContext);
  const onBackRef = useRef(onBack);
  const hasBack = typeof onBack === 'function';
  const setBackAction = ctx?.setBackAction;

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    if (!setBackAction) return undefined;
    if (!hasBack) {
      setBackAction(null);
      return () => setBackAction(null);
    }
    setBackAction({
      onClick: () => onBackRef.current?.(),
    });
    return () => setBackAction(null);
  }, [hasBack, setBackAction]);
}

export function useMobilePageHeaderOverride() {
  return useContext(MobilePageHeaderContext)?.overrideTitle ?? null;
}

export function useMobilePageHeaderTrailingAction() {
  return useContext(MobilePageHeaderContext)?.trailingAction ?? null;
}

export function useMobilePageHeaderBackAction() {
  return useContext(MobilePageHeaderContext)?.backAction ?? null;
}
